import { Colors, ConfirmDialog, Pagination, Spinner } from '@amelio/ui-library';
import { useState } from 'react';

import { ManagerNotesCard } from './ManagerNotesCard';
import { NoteComposerCard } from './NoteComposerCard';
import { ALL_FOLDERS, NoteFolderTabs } from './NoteFolderTabs';
import {
    GROWTH_GLASS_SURFACE,
    GROWTH_PANEL_SURFACE,
    GROWTH_PLACEHOLDER,
    GROWTH_SECTION_PADDING,
    GROWTH_SECTION_TITLE,
} from '../GrowthTab/growthStyles';

import type { NoteFolder } from './NoteFolderTabs';
import type { User } from '@/types/directory/User';

import { NoteCard } from '@/components/EmployeeNotes/NoteCard';
import { useEmployeeNoteList } from '@/components/EmployeeNotes/useEmployeeNoteList';
import { useAmelioTranslations } from '@/hooks';
import { EMPLOYEE_NOTES_PAGE_SIZE, NOTES_TAB_CATEGORIES } from '@/types/employee-note';

interface NotesTabProps {
    employeeId: string;
    /** The employee whose profile is open; `undefined` while the directory answers. */
    employee?: User;
    dataTestId?: string;
}

/**
 * The "Notes et rétroaction" tab: the manager's intro card beside the employee's notes, filed by
 * folder, paged by the server, written in place. Every folder is readable here, and a new note can
 * go in any of them.
 *
 * The editor stands under the notes at all times: a tab opened to write in asks for nothing before
 * it can be written in.
 */
export const NotesTab = ({ employeeId, employee, dataTestId = 'notes-tab' }: NotesTabProps) => {
    const { t } = useAmelioTranslations();

    const [folder, setFolder] = useState<NoteFolder>(ALL_FOLDERS);
    const category = folder === ALL_FOLDERS ? null : folder;

    const {
        notes,
        authorsById,
        isLoading,
        isError,
        isForbidden,
        page,
        setPage,
        totalCount,
        totalPages,
        pageRange,
        draftKey,
        startNewDraft,
        createNote,
        isCreating,
        updateNote,
        isSavingNote,
        noteIdToDelete,
        setNoteIdToDelete,
        confirmDelete,
        isDeleting,
    } = useEmployeeNoteList({
        employeeId,
        category,
        pageSize: EMPLOYEE_NOTES_PAGE_SIZE,
        writeErrors: {
            createError: t('performanceProfile.notesTab.createError'),
            updateError: t('performanceProfile.notesTab.updateError'),
            deleteError: t('performanceProfile.notesTab.deleteError'),
        },
        announceWrites: true,
        // The list follows a new note to its folder, where it is the newest.
        onCreated: (draft) => setFolder(draft.category),
    });

    const selectFolder = (next: NoteFolder) => {
        setFolder(next);
        setPage(1);
    };

    const editorPlaceholder = t('performanceProfile.notesTab.placeholder');

    return (
        <div
            // On wide screens the intro card keeps a 369px column and its own height, and the notes
            // start 100px lower: the offset is what makes the composition.
            className={'grid gap-6 lg:grid-cols-[369px_minmax(0,1fr)] lg:items-start lg:gap-12'}
            data-testid={dataTestId}
        >
            <ManagerNotesCard
                employeeFirstName={employee?.firstName ?? ''}
                dataTestId={`${dataTestId}-intro`}
            />

            <section
                className={`${GROWTH_GLASS_SURFACE} ${GROWTH_SECTION_PADDING} flex flex-col gap-6 lg:mt-[100px]`}
                data-testid={`${dataTestId}-notes`}
            >
                <h3 className={GROWTH_SECTION_TITLE}>{t('performanceProfile.notesTab.title')}</h3>

                <NoteFolderTabs
                    selected={folder}
                    onSelect={selectFolder}
                    dataTestId={`${dataTestId}-folders`}
                />

                {isLoading && (
                    <div className={`${GROWTH_PANEL_SURFACE} flex justify-center p-6`}>
                        <Spinner
                            size={'md'}
                            color={Colors.PRIMARY}
                        />
                    </div>
                )}

                {/*
                    An unread list is not an empty one: say so, and offer no way to write. A refusal
                    is not a failure either -- the notes are gated on their own permission, so a
                    reader of the profile may well be refused these while everything else answers.
                */}
                {isError && (
                    <p className={`${GROWTH_PANEL_SURFACE} ${GROWTH_PLACEHOLDER} p-6 text-center`}>
                        {isForbidden
                            ? t('performanceProfile.notesTab.forbidden')
                            : t('performanceProfile.notesTab.loadError')}
                    </p>
                )}

                {!isLoading && !isError && (
                    <>
                        {notes.length === 0 ? (
                            <p
                                className={`${GROWTH_PANEL_SURFACE} ${GROWTH_PLACEHOLDER} p-6 text-center`}
                            >
                                {t('performanceProfile.notesTab.empty')}
                            </p>
                        ) : (
                            <div
                                className={`${GROWTH_PANEL_SURFACE} flex flex-col gap-3 p-3 md:gap-4 md:p-6 lg:p-10`}
                            >
                                {notes.map((note) => (
                                    <NoteCard
                                        key={note.id}
                                        note={note}
                                        author={
                                            note.authorId
                                                ? authorsById.get(note.authorId)
                                                : undefined
                                        }
                                        showCategory={folder === ALL_FOLDERS}
                                        selectableCategories={NOTES_TAB_CATEGORIES}
                                        editorPlaceholder={editorPlaceholder}
                                        onSave={(draft) => updateNote(note, draft)}
                                        onRequestDelete={() => setNoteIdToDelete(note.id)}
                                        isSaving={isSavingNote(note.id)}
                                        dataTestId={`${dataTestId}-note-${note.id}`}
                                    />
                                ))}
                            </div>
                        )}

                        {totalCount > EMPLOYEE_NOTES_PAGE_SIZE && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalCount={totalCount}
                                pageSize={EMPLOYEE_NOTES_PAGE_SIZE}
                                onPageChange={setPage}
                                resultsSummaryText={t(
                                    'performanceProfile.notesTab.pageSummary',
                                    pageRange,
                                )}
                                dataTestId={`${dataTestId}-pagination`}
                            />
                        )}
                    </>
                )}

                {!isError && (
                    <NoteComposerCard
                        key={draftKey}
                        heading={t('performanceProfile.notesTab.add')}
                        // A new note starts in the folder being read, or the first one when every
                        // folder is on screen.
                        category={category ?? 'General'}
                        selectableCategories={NOTES_TAB_CATEGORIES}
                        placeholder={editorPlaceholder}
                        onSave={createNote}
                        onCancel={startNewDraft}
                        isSaving={isCreating}
                        dataTestId={`${dataTestId}-composer`}
                    />
                )}

                <ConfirmDialog
                    isOpen={!!noteIdToDelete}
                    title={t('performanceProfile.notesTab.deleteTitle')}
                    message={t('performanceProfile.notesTab.deleteMessage')}
                    confirmLabel={t('performanceProfile.employeeNotes.delete')}
                    cancelLabel={t('common.cancel')}
                    onConfirm={confirmDelete}
                    onCancel={() => setNoteIdToDelete(null)}
                    isLoading={isDeleting}
                    dataTestId={`${dataTestId}-delete-dialog`}
                />
            </section>
        </div>
    );
};
